/**
 * Centralized color utilities for consistent styling across the application.
 */

// ============================================
// TEXT COLORS - Semantic text color system
// ============================================

export const TEXT_COLORS = {
  PRIMARY: '#ffffff',                      // Main text
  SECONDARY: 'rgba(255, 255, 255, 0.6)',   // Secondary text
  TERTIARY: 'rgba(255, 255, 255, 0.45)',   // Tertiary text
  LIGHT: 'rgba(255, 255, 255, 0.9)',       // Light text for cards
  SOFT: 'rgba(255, 255, 255, 0.8)',        // Soft text (80%)
  MUTED: 'rgba(255, 255, 255, 0.7)',       // Muted text
  DISABLED: 'rgba(255, 255, 255, 0.5)',    // Disabled text
  CAPTION: 'rgba(255, 255, 255, 0.5)',     // Caption/label text
  LIMIT: '#ef4444',                        // Purchase limits, availability dates
} as const;

// ============================================
// BACKGROUND COLORS - Glass morphism system
// ============================================

export const BG_COLORS = {
  GLASS: 'rgba(255, 255, 255, 0.08)',        // Base glass background
  GLASS_LIGHT: 'rgba(255, 255, 255, 0.1)',   // Lighter glass
  GLASS_LIGHTER: 'rgba(255, 255, 255, 0.12)', // Even lighter
  GLASS_DARK: 'rgba(255, 255, 255, 0.05)',   // Darker glass
  GLASS_DARKER: 'rgba(255, 255, 255, 0.03)', // Darkest glass (card items)
  GLASS_HOVER: 'rgba(255, 255, 255, 0.06)',  // Hover for dark glass items
  HOVER: 'rgba(255, 255, 255, 0.15)',        // Hover state
  SELECTED: 'rgba(255, 255, 255, 0.2)',      // Selected state
  SURFACE: '#141414',                        // Paper/card surface
  CARD: 'rgba(0, 0, 0, 0.49)',              // Glass container background
  SUCCESS_LIGHT: 'rgba(34, 197, 94, 0.1)',   // Success background
  ERROR_LIGHT: 'rgba(239, 68, 68, 0.1)',     // Error background
  WARNING_LIGHT: 'rgba(245, 158, 11, 0.1)',  // Warning background
  INFO_LIGHT: 'rgba(59, 130, 246, 0.1)',     // Info background
} as const;

// ============================================
// BORDER COLORS - Border system
// ============================================

export const BORDER_COLORS = {
  DEFAULT: 'rgba(255, 255, 255, 0.1)',      // Default border
  LIGHT: 'rgba(255, 255, 255, 0.12)',       // Light border
  LIGHTER: 'rgba(255, 255, 255, 0.15)',     // Lighter border
  STRONG: 'rgba(255, 255, 255, 0.2)',       // Strong/visible border
  FOCUS: 'rgba(255, 255, 255, 0.5)',        // Focus state
  ACTIVE: 'rgba(255, 255, 255, 0.6)',       // Active state
  SUCCESS: 'rgba(34, 197, 94, 0.3)',        // Success border
  ERROR: 'rgba(239, 68, 68, 0.3)',          // Error border
  WARNING: 'rgba(245, 158, 11, 0.3)',       // Warning border
} as const;

// ============================================
// SCROLLBAR COLORS - Custom scrollbar system
// ============================================

export const SCROLLBAR_COLORS = {
  TRACK: 'transparent',
  THUMB: 'linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
  THUMB_HOVER: 'linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.25) 100%)',
  THUMB_SOLID: 'rgba(255, 255, 255, 0.2)',        // Fallback для Firefox
  THUMB_HOVER_SOLID: 'rgba(255, 255, 255, 0.35)', // Fallback для Firefox
} as const;

// ============================================
// WIKI COLORS - Soft colors for wiki components
// ============================================

export const WIKI_COLORS = {
  RED: {
    background: 'rgba(244, 67, 54, 0.12)',
    text: 'rgba(229, 115, 115, 1)',
  },
  GREEN: {
    background: 'rgba(76, 175, 80, 0.12)',
    text: 'rgba(129, 199, 132, 1)',
  },
  AMBER: {
    background: 'rgba(245, 158, 11, 0.12)',
    text: 'rgba(252, 211, 77, 1)',
  },
  BLUE: {
    background: 'rgba(99, 102, 241, 0.12)',
    text: 'rgba(165, 180, 252, 1)',
  },
  GRAY: {
    background: 'rgba(156, 163, 175, 0.12)',
    text: 'rgba(156, 163, 175, 1)',
  },
} as const;

// ============================================
// BOOLEAN CHIP COLORS - For true/false indicators
// ============================================

export const BOOLEAN_CHIP_COLORS = {
  TRUE: {
    background: 'rgba(76, 175, 80, 0.12)',
    text: 'rgba(129, 199, 132, 1)',
  },
  FALSE: {
    background: 'rgba(244, 67, 54, 0.12)',
    text: 'rgba(229, 115, 115, 1)',
  },
} as const;

// ============================================
// ACTION COLORS - Interactive element colors
// ============================================

export const ACTION_COLORS = {
  VIEW: 'rgba(255, 255, 255, 0.6)',       // Gray - view/details
  EDIT: 'rgba(255, 255, 255, 0.6)',       // Gray - edit
  DELETE: 'rgba(255, 255, 255, 0.6)',     // Gray - delete
  COPY: 'rgba(255, 255, 255, 0.6)',       // Gray - copy
  ADD: '#22c55e',                         // Green - add
  MONEY: 'rgba(255, 255, 255, 0.6)',      // Gray - money
  TOKENS: 'rgba(255, 255, 255, 0.6)',     // Gray - tokens
  WAREHOUSE: 'rgba(255, 255, 255, 0.6)',  // Gray - warehouse
  ROLE: 'rgba(255, 255, 255, 0.6)',       // Gray - role/admin
} as const;

// ============================================
// RANK COLORS - Item rank gradients
// ============================================

// Rank color gradients for items
export const RANK_COLORS = {
  REL: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',    // Pink/magenta (highest)
  TOP: 'linear-gradient(135deg, #ff9a56 0%, #ff6b6b 100%)',    // Orange to red
  HIGH: 'linear-gradient(135deg, #a8e6cf 0%, #4ecdc4 100%)',   // Light teal to teal
  MIDDLE: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)', // Light blue to blue
  LOW: 'linear-gradient(135deg, #b2bec3 0%, #636e72 100%)',    // Light gray to dark gray
  NONE: 'linear-gradient(135deg, #ddd6fe 0%, #8b5cf6 100%)',   // Light purple to purple
} as const;

// Crystal/Grade colors for items
export const GRADE_COLORS = {
  S: '#ff6b6b',    // Red
  A: '#4ecdc4',    // Teal
  B: '#45b7d1',    // Blue
  C: '#96ceb4',    // Green
  D: '#ffeaa7',    // Yellow
} as const;

/**
 * Цвета ролей пользователя. Один источник для всех чипов роли: список пользователей,
 * бейдж в сайдбаре админки, конфиги лаунчера. Раньше палитра лежала внутри
 * `AdminUsersPage`, а сайдбар рисовал свой сплошной красный градиент.
 */
export const USER_ROLE_COLORS: Record<string, string> = {
  UNVERIFIED_USER: '#6b7280',
  VERIFIED_USER: '#3b82f6',
  INFLUENCER: '#f59e0b',
  QA: '#14b8a6',
  SUPPORT: '#8b5cf6',
  ADMIN: '#ef4444',
  OWNER: '#dc2626',
};

// Расы персонажей игроков (чип расы): «Мои аккаунты», таб «Персонажи» в админке
export const PLAYER_RACE_COLORS: Record<string, { background: string; color: string }> = {
  HUMAN: { background: 'rgba(96, 165, 250, 0.15)', color: '#93bbfd' },
  ELF: { background: 'rgba(52, 211, 153, 0.15)', color: '#6ee7b7' },
  DARK_ELF: { background: 'rgba(168, 85, 247, 0.15)', color: '#c4b5fd' },
  ORC: { background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5' },
  DWARF: { background: 'rgba(245, 158, 11, 0.15)', color: '#fcd34d' },
};

// NPC race colors
export const RACE_COLORS: Record<string, string> = {
  HUMANOID: 'rgba(255, 255, 255, 0.6)',
  UNDEAD: 'rgba(255, 255, 255, 0.45)',
  BEAST: '#f59e0b',
  PLANT: '#22c55e',
  BUG: '#ef4444',
  DRAGON: '#dc2626',
  ANGEL: '#f59e0b',
  DEMON: '#b91c1c',
  GIANT: 'rgba(255, 255, 255, 0.4)',
  ELEMENTAL: 'rgba(255, 255, 255, 0.6)',
  FAIRY: 'rgba(255, 255, 255, 0.6)',
  CONSTRUCT: 'rgba(255, 255, 255, 0.45)',
  DIVINE: '#f59e0b',
  ETC: 'rgba(255, 255, 255, 0.45)',
} as const;

// Offer type colors for shop items
export const OFFER_TYPE_COLORS = {
  HOT: {
    background: '#ef4444',
    text: '#ffffff',
  },
  NEW: {
    background: '#22c55e',
    text: '#ffffff',
  },
  SALE: {
    background: '#f59e0b',
    text: '#ffffff',
  },
  DEFAULT: {
    background: '#dc2626',
    text: '#ffffff',
  },
} as const;

// Status colors
export const STATUS_COLORS = {
  ACTIVE: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    text: '#ffffff',
  },
  INACTIVE: {
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    text: '#ffffff',
  },
  PENDING: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    text: '#ffffff',
  },
  ERROR: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    text: '#ffffff',
  },
} as const;

// ============================================
// ACCENT COLORS - Primary red accent system
// ============================================

export const ACCENT_COLORS = {
  // Solid colors
  PRIMARY: '#dc2626',           // Main red
  PRIMARY_DARK: '#b91c1c',      // Dark red
  PRIMARY_LIGHT: '#ef4444',     // Light red

  // Gradients
  GRADIENT_DEFAULT: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
  GRADIENT_HOVER: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',

  // With transparency (rgba based on #b91c1c = rgb(185, 28, 28))
  BG_LIGHT: 'rgba(185, 28, 28, 0.15)',
  BG_HOVER: 'rgba(185, 28, 28, 0.3)',
  BORDER: 'rgba(185, 28, 28, 0.5)',
  GLOW: 'rgba(185, 28, 28, 0.4)',
  GLOW_STRONG: 'rgba(220, 38, 38, 0.6)',
} as const;

// Button gradient colors
export const BUTTON_GRADIENTS = {
  PRIMARY: {
    default: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    hover: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  },
  ERROR: {
    default: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    hover: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
  },
  WARNING: {
    default: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    hover: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
  },
  SUCCESS: {
    default: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    hover: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
  },
} as const;

/**
 * Get gradient color for item rank.
 * @param rank - The rank string (TOP, HIGH, MIDDLE, LOW, NONE)
 * @returns CSS gradient string
 */
export const getRankColor = (rank?: string): string => {
  return RANK_COLORS[rank as keyof typeof RANK_COLORS] || RANK_COLORS.NONE;
};

/**
 * Get color for item grade.
 * @param grade - The grade string (S, A, B, C, D)
 * @returns CSS color string
 */
export const getGradeColor = (grade?: string): string => {
  return GRADE_COLORS[grade as keyof typeof GRADE_COLORS] || 'default';
};

/**
 * Get color for NPC race.
 * @param race - The race string
 * @returns CSS color string
 */
export const getRaceColor = (race: string): string => {
  return RACE_COLORS[race] || 'rgba(255, 255, 255, 0.45)';
};

/**
 * Get colors for offer type chip.
 * @param offerType - The offer type string (HOT, NEW, SALE, etc.)
 * @returns Object with background and text colors
 */
export const getOfferTypeColors = (offerType?: string): { background: string; text: string } => {
  return OFFER_TYPE_COLORS[offerType as keyof typeof OFFER_TYPE_COLORS] || OFFER_TYPE_COLORS.DEFAULT;
};

/**
 * Get colors for status chip.
 * @param status - The status string (ACTIVE, INACTIVE, PENDING, ERROR)
 * @returns Object with background gradient and text colors
 */
export const getStatusColors = (status?: string): { background: string; text: string } => {
  return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.INACTIVE;
};

/**
 * Get button gradient colors.
 * @param variant - The button variant (PRIMARY, ERROR, WARNING, SUCCESS)
 * @returns Object with default and hover gradients
 */
export const getButtonGradient = (variant: keyof typeof BUTTON_GRADIENTS): { default: string; hover: string } => {
  return BUTTON_GRADIENTS[variant] || BUTTON_GRADIENTS.PRIMARY;
};

// ============================================
// HELPER FUNCTIONS FOR NEW COLOR SYSTEMS
// ============================================

/**
 * Get text color by semantic type.
 * @param type - The text type (PRIMARY, SECONDARY, TERTIARY, LIGHT, MUTED, DISABLED, CAPTION)
 * @returns CSS color string
 */
export const getTextColor = (type: keyof typeof TEXT_COLORS): string => {
  return TEXT_COLORS[type];
};

/**
 * Get background color by semantic type.
 * @param type - The background type (GLASS, GLASS_LIGHT, etc.)
 * @returns CSS color string
 */
export const getBgColor = (type: keyof typeof BG_COLORS): string => {
  return BG_COLORS[type];
};

/**
 * Get border color by semantic type.
 * @param type - The border type (DEFAULT, LIGHT, LIGHTER, STRONG, FOCUS, ACTIVE)
 * @returns CSS color string
 */
export const getBorderColor = (type: keyof typeof BORDER_COLORS): string => {
  return BORDER_COLORS[type];
};

/**
 * Get action color for interactive elements.
 * @param action - The action type (VIEW, EDIT, DELETE, etc.)
 * @returns CSS color string
 */
export const getActionColor = (action: keyof typeof ACTION_COLORS): string => {
  return ACTION_COLORS[action];
};
